import React, { useMemo, useState } from 'react';
import { listVersions, getVersionLabel, cardOf, lateQuarterOf } from './storage';
import { EstimateVersion, formatEstimateStream } from './types';

interface Props {
  /** Bumped after upload so we re-read localStorage. */
  refreshKey: number;
}

interface PivotedRow {
  program: string;
  day: string;
  demo: string;
  universe: number | null;
  vpvh: number | null;
  imps: (number | null)[];
  rtg: (number | null)[];
}

function pivotVersion(v: EstimateVersion): PivotedRow[] {
  const byKey = new Map<string, PivotedRow>();
  const quarterIdxByLabel = new Map<string, number>();
  v.quarters.forEach((q, i) => quarterIdxByLabel.set(q.label, i));

  for (const dbKey of Object.keys(v.estimateDb)) {
    const parts = dbKey.split('|');
    if (parts.length !== 4) continue;
    const [program, day, period, demoRtg] = parts;
    const demo = demoRtg.replace(/RTG$/, '');
    const qi = quarterIdxByLabel.get(period);
    if (qi == null) continue;
    const entry = v.estimateDb[dbKey];

    const rowKey = `${program}|${day}|${demo}`;
    let row = byKey.get(rowKey);
    if (!row) {
      row = {
        program, day, demo,
        universe: v.universeData[demo] ?? null,
        vpvh: entry.p ?? null,
        imps: new Array(v.quarters.length).fill(null),
        rtg: new Array(v.quarters.length).fill(null),
      };
      byKey.set(rowKey, row);
    }
    row.imps[qi] = entry.t;
    row.rtg[qi] = entry.x;
  }

  const dayOrder = ['M-F', 'M-Su', 'Wknd'];
  const out: PivotedRow[] = [];
  byKey.forEach(r => out.push(r));
  out.sort((a, b) => {
    if (a.program !== b.program) return a.program.localeCompare(b.program);
    const ai = dayOrder.indexOf(a.day);
    const bi = dayOrder.indexOf(b.day);
    if (ai !== bi) return ai - bi;
    return a.demo.localeCompare(b.demo);
  });
  return out;
}

const VersionsTab: React.FC<Props> = ({ refreshKey }) => {
  const versions = useMemo(() => listVersions(), [refreshKey]);
  const [selectedId, setSelectedId] = useState<number | null>(
    () => listVersions()[0]?.id ?? null,
  );

  React.useEffect(() => {
    if (versions.length === 0) { setSelectedId(null); return; }
    if (selectedId == null || !versions.find(v => v.id === selectedId)) {
      setSelectedId(versions[0].id);
    }
  }, [versions, selectedId]);

  const selected = versions.find(v => v.id === selectedId) ?? null;

  if (versions.length === 0) {
    return (
      <div style={{
        padding: '64px 32px',
        textAlign: 'center',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12,
          background: '#f1f5f9', color: '#94a3b8',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, marginBottom: 16,
        }}>
          ⊟
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
          No estimate versions yet
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          Use the <strong>Upload</strong> tab to upload the first version.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: 540 }}>
      {/* Left rail */}
      <div style={{
        flex: '0 0 240px',
        borderRight: '1px solid #e2e8f0',
        padding: '20px 12px',
        background: '#fafbfc',
        borderBottomLeftRadius: 8,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          margin: '0 8px 10px',
        }}>
          All Versions &middot; {versions.length}
        </div>
        {versions.map(v => {
          const isSelected = v.id === selectedId;
          const isCustomCard = cardOf(v) !== 'general';
          // Most uploads for a season cover four quarters; the extended file
          // MVMS ships later adds a fifth (4Q'27 on a 26/27 file). Without a
          // marker this rail can't tell them apart — every row just reads
          // "Upfront · N rows" and you have to click each one and read the
          // Quarters stat on the right to find the one that has it.
          const lateQ = lateQuarterOf(v);
          return (
            <button
              key={v.id}
              onClick={() => setSelectedId(v.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', textAlign: 'left',
                padding: '10px 12px', marginBottom: 4,
                background: isSelected ? '#eff6ff' : 'transparent',
                border: `1px solid ${isSelected ? '#bfdbfe' : 'transparent'}`,
                borderRadius: 6, cursor: 'pointer',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
              {/* Custom rate cards get a violet chip so the two upload
                  streams are separable at a glance — General and WPP
                  interleave in this list by date, and "V1" alone doesn't
                  say which sequence it belongs to. */}
              <div style={{
                width: 32, height: 32, borderRadius: 6,
                background: isCustomCard
                  ? (isSelected ? '#ede9fe' : '#f3f0ff')
                  : (isSelected ? '#dbeafe' : '#e2e8f0'),
                color: isCustomCard
                  ? '#6d28d9'
                  : (isSelected ? '#1e40af' : '#475569'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, flexShrink: 0,
              }}>
                {getVersionLabel(v)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: '#0f172a',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {formatDate(v.asOfDate)}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  <span style={{
                    color: isCustomCard ? '#7c3aed' : '#64748b', fontWeight: 600,
                  }}>
                    {formatEstimateStream(v.kind, cardOf(v))}
                  </span>
                  {lateQ && (
                    <>
                      {' · '}
                      <span
                        style={{ color: '#047857', fontWeight: 700 }}
                        title={`Includes ${lateQ.label} — the late quarter most uploads for this season don't carry.`}>
                        +{lateQ.label}
                      </span>
                    </>
                  )}
                  {' · '}
                  {v.rowCount.toLocaleString()} rows
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Right pane */}
      <div style={{ flex: 1, minWidth: 0, padding: '24px 28px' }}>
        {selected ? <VersionDetail v={selected} /> : null}
      </div>
    </div>
  );
};

const VersionDetail: React.FC<{ v: EstimateVersion }> = ({ v }) => {
  const allRows = useMemo(() => pivotVersion(v), [v]);
  const [programFilter, setProgramFilter] = useState('');
  const [dayFilter, setDayFilter] = useState<string>('');
  const [demoFilter, setDemoFilter] = useState('');

  // Distinct values for the filter dropdowns. Derived from the version's
  // own row set so the options always match what's actually shown.
  const distinctPrograms = useMemo(() => {
    const s = new Set<string>();
    allRows.forEach(r => s.add(r.program));
    const arr: string[] = [];
    s.forEach(p => arr.push(p));
    return arr.sort();
  }, [allRows]);

  const distinctDemos = useMemo(() => {
    const s = new Set<string>();
    allRows.forEach(r => s.add(r.demo));
    const arr: string[] = [];
    s.forEach(d => arr.push(d));
    // HH first if present, then the rest alphabetically — matches how
    // people scan the list (HH is the most-used baseline).
    return arr.sort((a, b) => {
      if (a === 'HH' && b !== 'HH') return -1;
      if (b === 'HH' && a !== 'HH') return 1;
      return a.localeCompare(b);
    });
  }, [allRows]);

  const rows = useMemo(() => {
    return allRows.filter(r => {
      if (programFilter && r.program !== programFilter) return false;
      if (demoFilter && r.demo !== demoFilter) return false;
      if (dayFilter && r.day !== dayFilter) return false;
      return true;
    });
  }, [allRows, programFilter, demoFilter, dayFilter]);

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: 16,
        marginBottom: 18, paddingBottom: 16,
        borderBottom: '1px solid #e2e8f0',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
          }}>
            <span style={{
              padding: '3px 9px', borderRadius: 5,
              background: '#dbeafe', color: '#1e40af',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
            }}>
              {getVersionLabel(v)}
            </span>
            <h2 style={{
              fontSize: 18, fontWeight: 700, color: '#0f172a',
              margin: 0, letterSpacing: '-0.01em',
            }}>
              As of {formatDate(v.asOfDate)}
            </h2>
          </div>
          <div style={{
            fontSize: 12.5, color: '#64748b',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {v.fileName}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, flexShrink: 0 }}>
          <Stat label="Rows" value={v.rowCount.toLocaleString()} />
          <Stat label="Quarters" value={v.quarters.map(q => q.label).join(' · ')} />
          <Stat label="Uploaded" value={new Date(v.uploadedAt).toLocaleDateString()} />
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center',
        marginBottom: 12, flexWrap: 'wrap',
      }}>
        <select
          value={programFilter}
          onChange={e => setProgramFilter(e.target.value)}
          style={filterInput}
          title="Filter by program">
          <option value="">All programs</option>
          {distinctPrograms.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={dayFilter}
          onChange={e => setDayFilter(e.target.value)}
          style={{ ...filterInput, width: 130 }}
          title="Filter by day">
          <option value="">All days</option>
          <option value="M-F">M-F</option>
          <option value="M-Su">M-Su</option>
          <option value="Wknd">Wknd</option>
        </select>
        <select
          value={demoFilter}
          onChange={e => setDemoFilter(e.target.value)}
          style={filterInput}
          title="Filter by demo">
          <option value="">All demos</option>
          {distinctDemos.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <span style={{
          fontSize: 12, color: '#64748b',
          marginLeft: 'auto', whiteSpace: 'nowrap',
        }}>
          {rows.length.toLocaleString()} of {allRows.length.toLocaleString()} rows
        </span>
      </div>

      {/* Table */}
      <div style={{
        overflow: 'auto',
        maxHeight: 'calc(100vh - 360px)',
        border: '1px solid #e2e8f0', borderRadius: 6,
        background: '#fff',
      }}>
        <table style={{
          width: '100%', borderCollapse: 'separate', borderSpacing: 0,
          fontSize: 11.5, fontFamily: 'DM Mono, ui-monospace, monospace',
        }}>
          <thead>
            <tr>
              <Th sticky>Program</Th>
              <Th>Day</Th>
              <Th>Demo</Th>
              <Th>Universe</Th>
              <Th>VPVH</Th>
              <Th colSpan={v.quarters.length} group>Sales Estimate IMPS (000)</Th>
              <Th colSpan={v.quarters.length} group>Sales Estimate Rating</Th>
            </tr>
            <tr>
              <Th sticky sub />
              <Th sub /><Th sub /><Th sub /><Th sub />
              {v.quarters.map(q => <Th sub key={`i-${q.key}`}>{q.label}</Th>)}
              {v.quarters.map(q => <Th sub key={`r-${q.key}`}>{q.label}</Th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`${r.program}|${r.day}|${r.demo}`}
                style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <Td sticky strong rowBg={i % 2 === 0 ? '#fff' : '#fafbfc'}>{r.program}</Td>
                <Td muted>{r.day}</Td>
                <Td strong>{r.demo}</Td>
                <Td>{fmtInt(r.universe)}</Td>
                <Td>{fmtFloat(r.vpvh, 3)}</Td>
                {r.imps.map((n, qi) => <Td key={`i${qi}`}>{fmtInt(n)}</Td>)}
                {r.rtg.map((n, qi) => <Td key={`r${qi}`}>{fmtFloat(n, 3)}</Td>)}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5 + 2 * v.quarters.length}
                    style={{
                      padding: '32px 16px', textAlign: 'center',
                      color: '#94a3b8', fontSize: 13,
                    }}>
                  No rows match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{
      fontSize: 10.5, fontWeight: 600, color: '#94a3b8',
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2,
    }}>{label}</div>
    <div style={{
      fontSize: 13, fontWeight: 600, color: '#0f172a',
      whiteSpace: 'nowrap',
    }}>{value}</div>
  </div>
);

const filterInput: React.CSSProperties = {
  padding: '6px 10px', fontSize: 12.5,
  border: '1px solid #cbd5e1', borderRadius: 5,
  background: '#fff',
  width: 180,
  outline: 'none',
  fontFamily: 'inherit',
  color: '#0f172a',
};

const Th: React.FC<{
  children?: React.ReactNode;
  colSpan?: number;
  group?: boolean;
  sub?: boolean;
  sticky?: boolean;
}> = ({ children, colSpan, group, sub, sticky }) => (
  <th colSpan={colSpan} style={{
    padding: sub ? '5px 10px' : '8px 10px',
    background: group ? '#0f172a' : '#f8fafc',
    color: group ? '#fff' : '#475569',
    fontWeight: 600,
    fontSize: group ? 11 : (sub ? 10.5 : 11.5),
    borderBottom: '1px solid #e2e8f0',
    // Header alignment matches cell alignment (centered) for a cleaner look.
    textAlign: 'center',
    position: 'sticky',
    top: sub ? 28 : 0,
    left: sticky ? 0 : undefined,
    zIndex: sticky ? 3 : 2,
    whiteSpace: 'nowrap',
    letterSpacing: group ? '0.03em' : undefined,
    textTransform: group ? 'uppercase' : undefined,
  }}>{children}</th>
);

const Td: React.FC<{
  children?: React.ReactNode;
  sticky?: boolean;
  strong?: boolean;
  muted?: boolean;
  rowBg?: string;
}> = ({ children, sticky, strong, muted, rowBg }) => (
  <td style={{
    padding: '5px 10px',
    borderBottom: '1px solid #f1f5f9',
    color: muted ? '#94a3b8' : '#0f172a',
    fontWeight: strong ? 600 : 400,
    textAlign: 'center',
    position: sticky ? 'sticky' : undefined,
    left: sticky ? 0 : undefined,
    background: sticky ? rowBg : undefined,
    whiteSpace: 'nowrap',
  }}>{children}</td>
);

function fmtInt(n: number | null): string {
  if (n == null || isNaN(n)) return '—';
  return Math.round(n).toLocaleString();
}
function fmtFloat(n: number | null, digits: number): string {
  if (n == null || isNaN(n)) return '—';
  return n.toFixed(digits);
}
function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default VersionsTab;
